// Rich content for all 4 service pages
// Each page needs ~800+ words of unique content for SEO

export const servicePages = {
  'lead-generation': {
    slug: 'lead-generation',
    metaTitle: 'AI-Powered Lead Generation Services | SGS Global Media | Pune, India',
    metaDescription:
      'SGS Global Media delivers high-intent B2B lead generation using AI-powered targeting, paid ads, and optimised funnels. First leads in 14–21 days. Serving India, US, UK, Australia.',
    hero: {
      label: 'Lead Generation',
      heading: 'AI-Powered Lead Generation for B2B Businesses',
      subheading:
        'Stop chasing cold leads. We build multi-channel systems that attract, qualify, and deliver high-intent prospects directly into your pipeline — predictably, every month.',
    },
    overview:
      'Most businesses struggle with lead generation because they rely on one channel, use generic messaging, and have no system to qualify leads before they reach the sales team. SGS Global Media takes a fundamentally different approach: we research your ideal customer profile (ICP) deeply, build targeted campaigns across the channels they actually use, and layer AI-powered qualification on top — so your sales team only speaks to people who are already interested and able to buy. Our lead generation engagements combine paid advertising on Meta, Google, LinkedIn, and YouTube with custom lead magnets, high-converting landing pages, and automated follow-up sequences. Every campaign is built to feed a system, not produce a single spike of activity. The result is a predictable, scalable flow of qualified leads that compounds as we optimise over time. We have delivered lead generation results for B2B service businesses, SaaS companies, coaches, consultants, and e-commerce brands across India, the US, the UK, and Australia.',
    features: [
      {
        title: 'Ideal Customer Profile (ICP) Research',
        description:
          'We map your best-fit buyer — demographics, psychographics, job titles, pain points, and the platforms they spend time on — before writing a single ad.',
      },
      {
        title: 'Multi-Channel Paid Campaigns',
        description:
          'We run campaigns on Meta (Facebook & Instagram), Google Search & Display, LinkedIn, and YouTube — testing and scaling what works for your specific offer.',
      },
      {
        title: 'Lead Magnet Creation',
        description:
          'We design high-value lead magnets (guides, audits, calculators, mini-courses) that attract your ideal buyer and exchange genuine value for contact information.',
      },
      {
        title: 'Landing Page Design & Optimisation',
        description:
          'Purpose-built landing pages with clear value propositions, social proof, and frictionless forms — optimised continuously to improve cost-per-lead.',
      },
      {
        title: 'AI-Powered Lead Qualification',
        description:
          'Automated qualification flows using chatbots, form logic, and scoring models filter out tyre-kickers and surface only the leads worth your sales team\'s time.',
      },
      {
        title: 'Real-Time Reporting Dashboard',
        description:
          'A live dashboard shows impressions, clicks, cost-per-lead, lead quality scores, and pipeline value — so you always know exactly what your investment is producing.',
      },
    ],
    process: [
      {
        step: '01',
        title: 'Discovery & ICP Mapping',
        description:
          'We run a deep-dive session to understand your offer, ideal client, past results, and competitive landscape. We exit this phase with a documented ICP and channel strategy.',
      },
      {
        step: '02',
        title: 'Campaign Architecture',
        description:
          'We build your campaign structure: ad accounts, audience segments, creative briefs, landing page wireframes, and lead magnet assets — ready for launch.',
      },
      {
        step: '03',
        title: 'Launch & Test',
        description:
          'Campaigns go live. We run structured A/B tests on audiences, ad creative, and landing page copy — gathering data to identify winning combinations fast.',
      },
      {
        step: '04',
        title: 'Optimise & Scale',
        description:
          'Winning variants get scaled. Underperformers are cut. We continuously improve cost-per-lead and lead quality as the campaign matures.',
      },
      {
        step: '05',
        title: 'Report & Refine',
        description:
          'Weekly performance reviews with your team. Monthly strategy sessions to adjust targeting, budget allocation, and messaging based on what\'s converting downstream.',
      },
    ],
    faqs: [
      {
        q: 'What industries do you generate leads for?',
        a: 'We work with B2B service businesses, SaaS companies, coaches, consultants, financial services, real estate, healthcare clinics, and e-commerce brands. If there is a defined ideal customer and a proven offer, we can build a lead generation system around it.',
      },
      {
        q: 'How quickly will I see the first leads?',
        a: 'Most clients see their first qualified leads within 14–21 days of campaign launch. The first 30 days are a testing and optimisation phase. Meaningful pipeline growth — consistent lead volume at predictable cost — typically appears in the 45–90 day window as we identify and scale winning campaign combinations.',
      },
      {
        q: 'Do you manage ad spend or just strategy?',
        a: 'We handle everything: strategy, creative production, campaign setup, daily management, optimisation, and reporting. Your ad spend goes directly to the ad platforms (Meta, Google, LinkedIn). Our fee covers the strategy and management layer on top.',
      },
      {
        q: 'What is the minimum ad budget you recommend?',
        a: 'For meaningful testing and results, we recommend a minimum monthly ad spend of $1,500–$3,000 USD (or equivalent in INR/GBP/AUD) per channel. Lower budgets limit the data volume needed to optimise effectively. We will give you a specific recommendation based on your market and offer during discovery.',
      },
    ],
    schema: {
      name: 'AI-Powered Lead Generation',
      serviceType: 'Lead Generation',
      description:
        'High-intent B2B lead generation using AI-powered targeting, paid advertising on Meta, Google, LinkedIn and YouTube, and automated qualification systems.',
      url: 'https://sgsglobalmedia.com/services/lead-generation',
    },
  },

  'sales-funnels': {
    slug: 'sales-funnels',
    metaTitle: 'Sales Funnel Design & Optimisation | SGS Global Media | Pune, India',
    metaDescription:
      'SGS Global Media builds high-converting sales funnels — VSLs, booking flows, and email sequences engineered to turn cold traffic into paying clients. India, US, UK, Australia.',
    hero: {
      label: 'Sales Funnels',
      heading: 'High-Converting Sales Funnels That Turn Traffic Into Paying Clients',
      subheading:
        'A generic website is not a sales system. We architect VSLs, booking flows, and automated email sequences that convert cold traffic into qualified appointments and closed deals.',
    },
    overview:
      'Most businesses lose the majority of their leads not at the traffic stage, but at the conversion stage. A potential client visits the website, sees a wall of information with no clear next step, and leaves — forever. SGS Global Media builds conversion-focused sales funnels that guide prospects through a deliberate journey: from awareness to interest, from interest to decision, from decision to booked call or purchase. Our funnel architecture starts with understanding the psychology of your buyer — what they believe before they engage, what objections they hold, and what proof they need to take action. We then design each stage of the funnel to address those beliefs and objections systematically, using VSLs (video sales letters), long-form sales pages, application forms, booking systems, and multi-step email sequences. Every funnel we build is connected to analytics so we can measure exactly where people drop off and run experiments to improve conversion rates continuously. We have built funnels for high-ticket service businesses, coaching programmes, SaaS trials, and product launches — achieving consistent conversion improvements of 30–150% over baseline website performance.',
    features: [
      {
        title: 'Funnel Strategy & Mapping',
        description:
          'We map the complete buyer journey from first touchpoint to closed sale — identifying every decision point, objection, and conversion opportunity.',
      },
      {
        title: 'VSL Scripting & Direction',
        description:
          'We write high-converting video sales letter scripts built on proven frameworks — covering the hook, problem, solution, proof, offer, and CTA in the right sequence.',
      },
      {
        title: 'Sales Page Design & Copywriting',
        description:
          'Long-form and short-form sales pages designed with conversion-first principles: clear headline, compelling subheads, social proof placement, and a single focused CTA.',
      },
      {
        title: 'Email Automation Sequences',
        description:
          'Post-optin and post-call nurture sequences that follow up automatically with valuable content, objection-handling emails, and time-sensitive offers.',
      },
      {
        title: 'Booking System Integration',
        description:
          'Frictionless Calendly, Cal.com, or custom booking flows integrated directly into your funnel — so interested prospects can schedule calls without back-and-forth.',
      },
      {
        title: 'A/B Testing & Ongoing CRO',
        description:
          'Continuous split-testing of headlines, CTAs, page layouts, and email subject lines to systematically improve conversion rates over time.',
      },
    ],
    process: [
      {
        step: '01',
        title: 'Offer & Audience Analysis',
        description:
          'We analyse your offer, price point, audience sophistication, and competitive landscape to determine the optimal funnel type and messaging strategy.',
      },
      {
        step: '02',
        title: 'Funnel Architecture',
        description:
          'We design the complete funnel blueprint — every page, email, and decision point — before building anything. You approve the strategy before we write a word of copy.',
      },
      {
        step: '03',
        title: 'Copy & Creative Production',
        description:
          'We write all funnel copy, design page layouts, produce email sequences, and create any supporting assets (lead magnets, VSL scripts, social proof templates).',
      },
      {
        step: '04',
        title: 'Build & Integration',
        description:
          'We build the funnel on your preferred platform, connect your email system and CRM, and run end-to-end testing before sending live traffic.',
      },
      {
        step: '05',
        title: 'Test, Measure & Improve',
        description:
          'Post-launch, we monitor conversion metrics at every funnel stage, identify drop-off points, and run structured experiments to improve performance.',
      },
    ],
    faqs: [
      {
        q: 'What platforms do you build funnels on?',
        a: 'We build on GoHighLevel, ClickFunnels, Webflow, WordPress (Elementor/Divi), Framer, and custom React/Next.js builds. We recommend the platform based on your existing tech stack and long-term needs, not just what we prefer to use.',
      },
      {
        q: 'Do I need a VSL or will a text sales page work?',
        a: 'It depends on your offer, price point, and audience. VSLs typically outperform text pages for high-ticket offers ($2,000+) and audiences that are new to you. For warm audiences or lower-ticket products, a well-written text page often converts just as well. We will recommend the right format after reviewing your offer.',
      },
      {
        q: 'How long does it take to build a funnel?',
        a: 'A standard lead generation funnel (optin page + thank you page + email sequence) takes 2–3 weeks. A full high-ticket funnel with VSL, sales page, application, and email sequences takes 4–6 weeks. Timelines depend on how quickly you can provide feedback and approve assets.',
      },
      {
        q: 'What conversion rate can I expect?',
        a: 'Optin rates for cold traffic typically range from 20–45% depending on the offer and traffic quality. Sales page or booking page conversions from warm audiences range from 5–20%. We will set realistic benchmarks for your specific offer during discovery and improve from there.',
      },
    ],
    schema: {
      name: 'Sales Funnel Design & Optimisation',
      serviceType: 'Sales Funnel Development',
      description:
        'High-converting sales funnels including VSLs, booking flows, sales pages, and email automation sequences designed to convert cold traffic into paying clients.',
      url: 'https://sgsglobalmedia.com/services/sales-funnels',
    },
  },

  'crm-automation': {
    slug: 'crm-automation',
    metaTitle: 'CRM & AI Automation Services | SGS Global Media | Pune, India',
    metaDescription:
      'SGS Global Media implements CRM systems and AI-powered automation — GoHighLevel, HubSpot, n8n, Make.com — to keep your pipeline moving 24/7. India, US, UK, Australia.',
    hero: {
      label: 'CRM & AI Automation',
      heading: 'CRM & AI Automation That Keeps Your Pipeline Moving 24/7',
      subheading:
        'The average business loses 27% of its leads to slow follow-up. We implement CRM systems and AI-powered automations that follow up instantly, qualify leads automatically, and never let a prospect go cold.',
    },
    overview:
      'Most businesses collect leads but fail to convert them — not because of a bad offer, but because of slow follow-up and inconsistent nurturing. Research shows that a lead contacted within 5 minutes of enquiring is 9 times more likely to convert than one contacted after 30 minutes. Most businesses respond in hours or days. SGS Global Media solves this with CRM implementation and AI-powered automation that acts the moment a lead enters your system. We implement and customise leading CRM platforms including GoHighLevel, HubSpot, and Pipedrive to fit your sales process exactly. We then layer intelligent automation on top: AI chatbots that qualify leads around the clock, automated follow-up sequences triggered by lead behaviour, pipeline stage automations that move deals forward without manual input, and reporting dashboards that give you complete visibility into your sales pipeline at all times. For teams with existing CRM systems that are underutilised or misconfigured, we offer a CRM audit and rebuild service that transforms a stagnant system into an active revenue engine. We integrate with platforms including n8n, Make.com (formerly Integromat), Zapier, and custom API connections to connect your CRM to every tool in your stack.',
    features: [
      {
        title: 'CRM Platform Implementation',
        description:
          'We set up and configure GoHighLevel, HubSpot, Pipedrive, or your chosen CRM — customised to your sales process, pipeline stages, and team structure.',
      },
      {
        title: 'AI-Powered Lead Qualification',
        description:
          'AI chatbots and automated qualification flows engage leads instantly, ask the right discovery questions, and score leads before they reach your sales team.',
      },
      {
        title: 'Automated Follow-Up Sequences',
        description:
          'Multi-channel follow-up sequences (email, SMS, WhatsApp) triggered by lead behaviour — so no lead goes cold due to slow manual response.',
      },
      {
        title: 'Pipeline Automation',
        description:
          'Smart triggers that move deals through pipeline stages, assign tasks, send internal notifications, and escalate stalled deals automatically.',
      },
      {
        title: 'Workflow Automation (n8n / Make.com)',
        description:
          'Custom automation workflows that connect your CRM to your entire tech stack — ad platforms, booking tools, payment processors, Slack, email, and more.',
      },
      {
        title: 'Reporting & Analytics Dashboard',
        description:
          'Custom dashboards that show lead volume, conversion rates, pipeline value, response times, and revenue attribution — updated in real time.',
      },
    ],
    process: [
      {
        step: '01',
        title: 'CRM Audit & Requirements',
        description:
          'We audit your existing CRM (if any), document your sales process, and map every touchpoint from lead entry to closed deal — identifying automation opportunities.',
      },
      {
        step: '02',
        title: 'Platform Selection & Setup',
        description:
          'We recommend and configure the right CRM for your team size, sales complexity, and budget — custom fields, pipeline stages, user permissions, and integrations.',
      },
      {
        step: '03',
        title: 'Automation Design',
        description:
          'We design every automation workflow on paper before building — covering triggers, conditions, actions, delays, and fallback paths for every scenario.',
      },
      {
        step: '04',
        title: 'Build & Test',
        description:
          'We build all automations, connect integrations, and run end-to-end testing with real lead data to confirm every workflow fires correctly before going live.',
      },
      {
        step: '05',
        title: 'Team Training & Handover',
        description:
          'We train your team on using the CRM and automations, provide documentation, and offer a 30-day support period to resolve any issues post-launch.',
      },
    ],
    faqs: [
      {
        q: 'Which CRM platforms do you work with?',
        a: 'We specialise in GoHighLevel (ideal for agencies and service businesses), HubSpot (strong for scaling teams), and Pipedrive (excellent for straightforward sales pipelines). We also work with ActiveCampaign, Zoho CRM, and custom setups. We will recommend the right platform based on your team size, sales process, and budget.',
      },
      {
        q: 'Can you automate WhatsApp follow-ups?',
        a: 'Yes. We build WhatsApp automation through WhatsApp Business API integrations (via platforms like Wati, Interakt, or native GoHighLevel WhatsApp) combined with n8n or Make.com workflows. This is particularly effective for Indian and South-East Asian markets where WhatsApp has very high open rates.',
      },
      {
        q: 'We already have a CRM but it is barely used — can you help?',
        a: 'Absolutely. This is one of the most common situations we encounter. We run a CRM audit, clean up existing data, rebuild the pipeline structure, create the automations your team was missing, and deliver training to drive adoption. An underutilised CRM is often fixable without changing platforms.',
      },
      {
        q: 'How long does a full CRM implementation take?',
        a: 'A standard CRM setup with core automations (lead entry, follow-up sequences, pipeline stages) takes 2–4 weeks. A complex implementation with multiple integrations, custom workflows, and team training typically takes 4–8 weeks.',
      },
    ],
    schema: {
      name: 'CRM & AI Automation',
      serviceType: 'CRM Implementation and Marketing Automation',
      description:
        'CRM implementation, AI-powered lead qualification, automated follow-up sequences, and workflow automation using GoHighLevel, HubSpot, n8n, and Make.com.',
      url: 'https://sgsglobalmedia.com/services/crm-automation',
    },
  },

  'web-development': {
    slug: 'web-development',
    metaTitle: 'Conversion-Focused Web Development | SGS Global Media | Pune, India',
    metaDescription:
      'SGS Global Media builds high-converting, SEO-optimised websites and landing pages engineered to generate leads and grow revenue. India, US, UK, Australia.',
    hero: {
      label: 'Web Development',
      heading: 'High-Converting Websites Built to Generate Leads — Not Just Look Good',
      subheading:
        'Most websites are digital brochures. We build conversion-engineered web properties — landing pages, VSL pages, and full sites — designed from the first pixel to turn visitors into qualified leads.',
    },
    overview:
      'A website that does not generate leads is an expense, not an asset. The gap between a beautiful website and a high-converting website comes down to structure, messaging, page speed, and a deep understanding of what your visitor is thinking when they arrive. SGS Global Media builds web properties with conversion as the primary objective — not design awards. Every element is placed intentionally: the headline that immediately communicates value, the social proof positioned where trust is needed most, the CTA that appears at exactly the right moment in the user journey, and the page speed that prevents abandonment before the message lands. We build on React, Next.js, Webflow, and WordPress depending on your requirements — with a strong preference for static site generation (Next.js) for client-facing pages that need strong SEO performance and fast load times. All sites we deliver are optimised for Core Web Vitals (LCP, INP, CLS), mobile-first responsive across all device sizes, and structured with proper semantic HTML, meta tags, schema markup, and sitemap — so they rank as well as they convert. We have built web properties for marketing agencies, SaaS companies, consultants, e-commerce brands, and B2B service businesses across India, the US, the UK, and Australia.',
    features: [
      {
        title: 'Conversion-First Design',
        description:
          'Every layout decision — headline placement, CTA position, social proof, whitespace — is made to guide the visitor toward the desired action, not to win design awards.',
      },
      {
        title: 'SEO-Optimised Architecture',
        description:
          'Semantic HTML structure, proper heading hierarchy, meta tags, canonical URLs, schema markup, and XML sitemaps — built in from day one, not bolted on afterwards.',
      },
      {
        title: 'Core Web Vitals Optimisation',
        description:
          'We optimise LCP, INP, and CLS to meet Google\'s Good threshold — including image optimisation, code splitting, lazy loading, and server-side rendering where applicable.',
      },
      {
        title: 'Landing Pages & VSL Pages',
        description:
          'Standalone conversion pages for ad campaigns, product launches, and lead generation — built to convert specific traffic sources at the lowest cost-per-lead.',
      },
      {
        title: 'Integrations & APIs',
        description:
          'CRM connections (GoHighLevel, HubSpot), booking tools (Calendly, Cal.com), payment processors (Stripe, Razorpay), and custom API integrations built cleanly and reliably.',
      },
      {
        title: 'Mobile-First Responsive',
        description:
          'Every site is designed mobile-first, tested across iOS and Android devices, and optimised for the touch interactions and viewport sizes of your actual audience.',
      },
    ],
    process: [
      {
        step: '01',
        title: 'Discovery & Sitemap',
        description:
          'We define your goals, target audience, key pages, and conversion objectives. We build a sitemap and wireframe the site architecture before writing a line of code.',
      },
      {
        step: '02',
        title: 'Design & Copywriting',
        description:
          'We design high-fidelity mockups for key pages and write conversion-focused copy aligned with your ICP — getting your approval before development begins.',
      },
      {
        step: '03',
        title: 'Development & Integration',
        description:
          'We build the site with clean, maintainable code, integrate all required tools and APIs, and implement SEO foundations — schema, meta tags, sitemap, robots.txt.',
      },
      {
        step: '04',
        title: 'Performance & QA',
        description:
          'We run Google PageSpeed Insights, cross-browser testing, mobile QA, and Core Web Vitals checks — fixing every issue before handover.',
      },
      {
        step: '05',
        title: 'Launch & Post-Launch Support',
        description:
          'We deploy the site, submit to Google Search Console, and provide 30 days of post-launch support to address any issues and monitor performance.',
      },
    ],
    faqs: [
      {
        q: 'What technology do you build on?',
        a: 'We build primarily on Next.js (React) for sites that require strong SEO and performance, Webflow for design-heavy marketing sites, and WordPress with a custom build (not a pre-made theme) for clients who need a simple CMS. We recommend the right stack based on your requirements — not what is easiest for us.',
      },
      {
        q: 'How long does a website take to build?',
        a: 'A focused landing page or lead generation page takes 1–2 weeks. A full marketing website with 5–10 pages takes 4–6 weeks. A more complex web application or e-commerce site takes 8–16 weeks depending on scope. Timelines are driven heavily by how quickly client feedback and approvals arrive.',
      },
      {
        q: 'Will my website rank on Google?',
        a: 'We build in the technical SEO foundations that Google requires — semantic HTML, proper meta tags, schema markup, canonical URLs, XML sitemap, and Core Web Vitals optimisation. Ranking for competitive keywords also requires ongoing content creation and backlink building, which we offer as a separate service. The foundation we build makes that work far more effective.',
      },
      {
        q: 'Do you offer website maintenance after launch?',
        a: 'Yes. We offer monthly maintenance packages that cover security updates, plugin/dependency updates, performance monitoring, and up to 2 hours of content changes per month. Ask us about maintenance pricing during the project proposal stage.',
      },
    ],
    schema: {
      name: 'Conversion-Focused Web Development',
      serviceType: 'Web Development',
      description:
        'High-converting, SEO-optimised websites and landing pages built on React, Next.js, and Webflow — engineered to generate leads and pass Google Core Web Vitals.',
      url: 'https://sgsglobalmedia.com/services/web-development',
    },
  },
};
